import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Input, Row, TimePicker, notification } from 'antd';
import dayjs from 'dayjs';

import type { UpdateAppointmentTimeFormValues } from '../../../types';
import { updateAppointmentTime } from '../../../service/httpsCalls';

interface UpdateAppointmentTimeProps {
    initialValues?: Partial<UpdateAppointmentTimeFormValues>;
    onSuccess?: () => void;
}

const TIME_FORMAT = 'HH:mm';

const UpdateAppointmentTime: React.FC<UpdateAppointmentTimeProps> = ({
    initialValues,
    onSuccess,
}) => {
    const [form] = Form.useForm<UpdateAppointmentTimeFormValues>();
    const [submitting, setSubmitting] = useState(false);
    const [notificationApi, notificationContextHolder] = notification.useNotification();

    useEffect(() => {
        const pickValue = (...keys: string[]): string => {
            for (const key of keys) {
                const raw = (initialValues as Record<string, any> | undefined)?.[key];
                if (raw !== undefined && raw !== null && raw !== '') {
                    if (typeof raw === 'object') {
                        return '';
                    }
                    return String(raw);
                }
            }
            return '';
        };

        const timeValue = pickValue('newPickupTime', 'NewPickupTime', 'pickupTime', 'PickupTime');
        const parsedTime = timeValue ? dayjs(timeValue, TIME_FORMAT, true) : null;

        form.setFieldsValue({
            newPickupTime: parsedTime && parsedTime.isValid() ? parsedTime : null,
            standingOrderId: pickValue('standingOrderId', 'StandingOrderId', 'standingOrderID'),
            ctcTripId: pickValue('ctcTripId', 'CtcTripId', 'ctcTripID', 'CTCTripId', 'tripLegSeqNum', 'TripLegSeqNum'),
        });
    }, [initialValues, form]);

    const showNotification = (
        type: 'success' | 'error',
        message: string,
        description?: string,
    ) => {
        notificationApi[type]({
            message,
            description,
        });
    };

    const handleSubmit = async () => {
        try {
            const values = (await form.validateFields()) as UpdateAppointmentTimeFormValues;
            setSubmitting(true);
            await updateAppointmentTime(values);

            showNotification('success', 'Appointment time updated');
            setTimeout(() => {
                onSuccess?.();
            }, 200)
        } catch (error: any) {
            if (error?.errorFields) {
                return;
            }
            const serverMessage =
                error?.messages?.[0]?.description ??
                error?.message ??
                'Unable to update appointment time.';
            showNotification('error', 'Update failed', serverMessage);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {notificationContextHolder}
            <Form
                layout="vertical"
                form={form}
                autoComplete="off"
            >
                <Row gutter={[24, 12]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="New Pickup Time"
                            name="newPickupTime"
                            rules={[{ required: true, message: 'New Pickup Time is required' }]}
                        >
                            <TimePicker
                                format={TIME_FORMAT}
                                style={{ width: '100%', border: '1px solid #d9d9d9' }}
                                minuteStep={5}
                                allowClear={false}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Standing Order Id"
                            name="standingOrderId"
                        >
                            <Input
                                disabled
                                placeholder="Standing Order Id"
                                style={{ color: 'rgba(0,0,0,0.88)', border: '1px solid #d9d9d9' }}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Ctc Trip Id"
                            name="ctcTripId"
                        >
                            <Input
                                disabled
                                placeholder="Ctc Trip Id"
                                style={{ color: 'rgba(0,0,0,0.88)', border: '1px solid #d9d9d9' }}
                            />
                        </Form.Item>
                    </Col>
                    <Col
                        xs={24}
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            type="primary"
                            htmlType="button"
                            onClick={handleSubmit}
                            loading={submitting}
                        >
                            Update
                        </Button>
                    </Col>
                </Row>
            </Form>
        </>
    );
};

export default UpdateAppointmentTime;
